"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const Box_1 = __importDefault(require("@mui/material/Box"));
const Typography_1 = __importDefault(require("@mui/material/Typography"));
const Alert_1 = __importDefault(require("@mui/material/Alert"));
const calendar_controller_1 = require("@eagle-projects/calendar-controller");
require("@eagle-projects/calendar-controller/src/styles/calendar.css");
const shared_1 = require("../shared");
const calendarApi_1 = require("./calendarApi");
const defaultTimezone = (() => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    catch (_a) {
        return 'America/New_York';
    }
})();
const CalendarDrawer = ({ open, tutors: tutorsProp = [], attendees: attendeesProp = [], timezone: timezoneProp, admin: adminProp, selectedTutorId: selectedTutorIdProp, onSelectedTutorIdChange, onSessionPanelOpenChange, setPendingUserId, pendingUserId, userData, }) => {
    const admin = adminProp !== null && adminProp !== void 0 ? adminProp : false;
    const timezone = timezoneProp !== null && timezoneProp !== void 0 ? timezoneProp : defaultTimezone;
    const attendees = attendeesProp !== null && attendeesProp !== void 0 ? attendeesProp : [];
    const [selectedTutorIdInternal, setSelectedTutorIdInternal] = (0, react_1.useState)('');
    const selectedTutorId = selectedTutorIdProp !== null && selectedTutorIdProp !== void 0 ? selectedTutorIdProp : selectedTutorIdInternal;
    const setSelectedTutorId = onSelectedTutorIdChange !== null && onSelectedTutorIdChange !== void 0 ? onSelectedTutorIdChange : setSelectedTutorIdInternal;
    const [classes, setClasses] = (0, react_1.useState)([]);
    const [events, setEvents] = (0, react_1.useState)([]);
    const [eventsLoading, setEventsLoading] = (0, react_1.useState)(false);
    const [apiLoading, setApiLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    // Determine the current tutor (selected tutor or first tutor)
    const tutor = (0, react_1.useMemo)(() => {
        var _a, _b, _c;
        if (!tutorsProp || tutorsProp.length === 0)
            return null;
        if (admin && selectedTutorId) {
            return (_b = (_a = tutorsProp.find((t) => t.userId === selectedTutorId)) !== null && _a !== void 0 ? _a : tutorsProp[0]) !== null && _b !== void 0 ? _b : null;
        }
        return (_c = tutorsProp[0]) !== null && _c !== void 0 ? _c : null;
    }, [admin, tutorsProp, selectedTutorId]);
    // Set initial selected tutor when tutors are loaded (only when using internal state)
    (0, react_1.useEffect)(() => {
        if (!onSelectedTutorIdChange && admin && tutorsProp.length > 0 && !selectedTutorId) {
            setSelectedTutorId(tutorsProp[0].userId);
        }
    }, [admin, tutorsProp, selectedTutorId, onSelectedTutorIdChange]);
    const fetchClasses = (0, react_1.useCallback)(async () => {
        console.log('fetchClasses', userData);
        if (!(tutor === null || tutor === void 0 ? void 0 : tutor.userId)) {
            setClasses([]);
            return;
        }
        setApiLoading(true);
        setError(null);
        try {
            const api = admin ? `class/by-organizer/${tutor === null || tutor === void 0 ? void 0 : tutor.userId}` : userData.userRole === 'tutor' ? `class/by-organizer/${userData.id}` : `class/by-participant/${userData.id}`;
            const res = await (0, shared_1.callApi)(api);
            if (!res.ok)
                throw new Error(`GET classes failed: ${res.status}`);
            const data = await res.json();
            setClasses(Array.isArray(data) ? data : []);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load classes');
            setClasses([]);
        }
        finally {
            setApiLoading(false);
        }
    }, [tutor === null || tutor === void 0 ? void 0 : tutor.userId]);
    (0, react_1.useEffect)(() => {
        if (open && (tutor === null || tutor === void 0 ? void 0 : tutor.userId))
            fetchClasses();
    }, [open, tutor === null || tutor === void 0 ? void 0 : tutor.userId, fetchClasses]);
    // Resolve classes to events (async: fetches organizer info via participantsFromClass)
    (0, react_1.useEffect)(() => {
        let cancelled = false;
        setEventsLoading(true);
        (0, calendarApi_1.classesToEvents)(classes, tutor !== null && tutor !== void 0 ? tutor : undefined, attendees)
            .then((evts) => {
            if (!cancelled)
                setEvents(evts);
        })
            .catch(() => {
            if (!cancelled)
                setEvents([]);
        })
            .finally(() => {
            if (!cancelled)
                setEventsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [classes, tutor, attendees]);
    const availabilitySlots = (0, react_1.useMemo)(() => {
        if (!(tutor === null || tutor === void 0 ? void 0 : tutor.availability))
            return [];
        const now = new Date();
        const startDate = new Date(now);
        startDate.setFullYear(startDate.getFullYear() - 2);
        const endDate = new Date(now);
        endDate.setFullYear(endDate.getFullYear() + 20);
        try {
            return (0, calendar_controller_1.generateAvailabilityFromUser)(tutor, startDate, endDate, timezone);
        }
        catch (_a) {
            return [];
        }
    }, [tutor, timezone]);
    const participants = (0, react_1.useMemo)(() => {
        const result = [];
        if (tutor)
            result.push((0, calendar_controller_1.userToParticipant)(tutor));
        attendees.forEach((a) => result.push((0, calendar_controller_1.attendeeToParticipant)(a)));
        return result;
    }, [tutor, attendees]);
    const handleCreateEvent = (0, react_1.useCallback)(async (data) => {
        const body = (0, calendarApi_1.buildPostBody)(data, tutor !== null && tutor !== void 0 ? tutor : undefined, timezone);
        setApiLoading(true);
        setError(null);
        try {
            const res = await (0, shared_1.callApi)('class', 'POST', body);
            if (!res.ok)
                throw new Error(`Create failed: ${res.status}`);
            await fetchClasses();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to create class');
        }
        finally {
            setApiLoading(false);
        }
    }, [tutor, timezone, fetchClasses]);
    const handleRsvpChange = (0, react_1.useCallback)(async (sessionId, status) => {
        setApiLoading(true);
        setError(null);
        try {
            const path = status === 'accepted' ? 'accept' : 'decline';
            const res = await (0, shared_1.callApi)(`sessions/${sessionId}/${path}/${userData === null || userData === void 0 ? void 0 : userData.id}`, 'POST');
            if (!res.ok)
                throw new Error(`RSVP ${status} failed: ${res.status}`);
            await fetchClasses();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to update RSVP status');
        }
        finally {
            setApiLoading(false);
        }
    }, [fetchClasses, userData === null || userData === void 0 ? void 0 : userData.id]);
    const handleUpdateEvent = (0, react_1.useCallback)(async (data) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        const classId = (_b = (_a = data.extendedProps) === null || _a === void 0 ? void 0 : _a.classId) !== null && _b !== void 0 ? _b : (0, calendarApi_1.findClassIdBySessionId)(classes, data.id);
        if (!classId) {
            setError('Cannot find class for this session');
            return;
        }
        setApiLoading(true);
        setError(null);
        const ext = data.extendedProps;
        const isCancel = (ext === null || ext === void 0 ? void 0 : ext.status) === 'canceled';
        if (isCancel) {
            try {
                // Only this session: PUT session/:id/cancel; entire series: DELETE class/:classId
                if (data.cancelScope === 'session') {
                    const cancelBody = { cancellationReason: ext === null || ext === void 0 ? void 0 : ext.cancellationReason };
                    const res = await (0, shared_1.callApi)(`session/${data.id}/cancel`, 'PUT', cancelBody);
                    if (!res.ok)
                        throw new Error(`Cancel session failed: ${res.status}`);
                }
                else {
                    const deleteRes = await (0, shared_1.callApi)(`class/${classId}`, 'DELETE');
                    if (!deleteRes.ok)
                        throw new Error(`Cancel failed: ${deleteRes.status}`);
                }
                await fetchClasses();
            }
            catch (e) {
                setError(e instanceof Error ? e.message : 'Failed to cancel');
            }
            finally {
                setApiLoading(false);
            }
            return;
        }
        const start = data.start != null ? (typeof data.start === 'string' ? new Date(data.start) : data.start) : new Date();
        const end = data.end != null ? (typeof data.end === 'string' ? new Date(data.end) : data.end) : new Date();
        const organizer = (_d = (_c = data.participants) === null || _c === void 0 ? void 0 : _c.find((p) => p.role === 'organizer')) !== null && _d !== void 0 ? _d : (_e = data.participants) === null || _e === void 0 ? void 0 : _e[0];
        const organizerId = (_h = (_g = (_f = organizer === null || organizer === void 0 ? void 0 : organizer.id) !== null && _f !== void 0 ? _f : userData === null || userData === void 0 ? void 0 : userData.userId) !== null && _g !== void 0 ? _g : userData === null || userData === void 0 ? void 0 : userData.id) !== null && _h !== void 0 ? _h : '';
        const participantsList = (_j = data.participants) !== null && _j !== void 0 ? _j : [];
        const participantIds = participantsList.filter((p) => p.id !== organizerId).map((p) => p.id);
        // Single occurrence reschedule (recurring event, user chose "Only this session")
        const updateSeries = data.updateSeries;
        if (updateSeries === false) {
            const durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
            const rescheduleBody = {
                organizerId,
                participantIds,
                startDateTime: start.toISOString(),
                endDateTime: end.toISOString(),
                timezone: (_l = (_k = ext === null || ext === void 0 ? void 0 : ext.timezone) !== null && _k !== void 0 ? _k : timezone) !== null && _l !== void 0 ? _l : undefined,
                notes: (_m = data.description) !== null && _m !== void 0 ? _m : undefined,
                status: 'rescheduled',
                durationMinutes,
                notificationSettings: {
                    notifyStudentEmail: (_o = ext === null || ext === void 0 ? void 0 : ext.notifyStudentEmail) !== null && _o !== void 0 ? _o : false,
                    notifyStudentSMS: (_p = ext === null || ext === void 0 ? void 0 : ext.notifyStudentSMS) !== null && _p !== void 0 ? _p : false,
                    notifyTutorEmail: (_q = ext === null || ext === void 0 ? void 0 : ext.notifyTutorEmail) !== null && _q !== void 0 ? _q : false,
                    notifyTutorSMS: (_r = ext === null || ext === void 0 ? void 0 : ext.notifyTutorSMS) !== null && _r !== void 0 ? _r : false,
                    reminders: ((_s = ext === null || ext === void 0 ? void 0 : ext.reminders) !== null && _s !== void 0 ? _s : []).map((r) => ({
                        type: r.type,
                        beforeMinutes: r.beforeMinutes,
                    })),
                    notifyTo: ((_t = ext === null || ext === void 0 ? void 0 : ext.notifyTo) !== null && _t !== void 0 ? _t : { mode: 'all' }),
                },
            };
            try {
                const res = await (0, shared_1.callApi)(`session/${data.id}/reschedule`, 'PUT', rescheduleBody);
                if (!res.ok)
                    throw new Error(`Reschedule session failed: ${res.status}`);
                await fetchClasses();
            }
            catch (e) {
                setError(e instanceof Error ? e.message : 'Failed to reschedule session');
            }
            finally {
                setApiLoading(false);
            }
            return;
        }
        const putBody = (0, calendarApi_1.buildPutBody)(data, tutor !== null && tutor !== void 0 ? tutor : undefined, timezone);
        try {
            const res = await (0, shared_1.callApi)(`class/${classId}`, 'PUT', putBody);
            if (!res.ok)
                throw new Error(`Update failed: ${res.status}`);
            await fetchClasses();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to update class');
        }
        finally {
            setApiLoading(false);
        }
    }, [classes, tutor, timezone, fetchClasses, userData]);
    const handleDeleteEvent = (0, react_1.useCallback)(async (eventId, updateSeries = true) => {
        const classId = (0, calendarApi_1.findClassIdBySessionId)(classes, eventId);
        if (!classId) {
            setError('Cannot find class for this session');
            return;
        }
        setApiLoading(true);
        setError(null);
        try {
            if (updateSeries === false) {
                const res = await (0, shared_1.callApi)(`session/${eventId}/permanent`, 'DELETE');
                if (!res.ok)
                    throw new Error(`Delete session failed: ${res.status}`);
            }
            else {
                const res = await (0, shared_1.callApi)(`class/${classId}/permanent`, 'DELETE');
                if (!res.ok)
                    throw new Error(`Delete failed: ${res.status}`);
            }
            await fetchClasses();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to delete class');
        }
        finally {
            setApiLoading(false);
        }
    }, [classes, fetchClasses]);
    console.log({ tutor, participants, attendees });
    const content = ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [error && ((0, jsx_runtime_1.jsx)(Alert_1.default, Object.assign({ severity: 'error', onClose: () => setError(null), sx: { mx: 2, mt: 1 } }, { children: error }))), (0, jsx_runtime_1.jsx)(Box_1.default, Object.assign({ sx: { flex: 1, overflow: 'hidden', padding: 2, display: 'flex', flexDirection: 'column', minHeight: 0 } }, { children: (tutor === null || tutor === void 0 ? void 0 : tutor.userId) ? ((0, jsx_runtime_1.jsx)(calendar_controller_1.CalendarController, { events: events, availability: availabilitySlots, participants: participants, user: tutor, attendees: attendees, onCreateEvent: handleCreateEvent, onUpdateEvent: handleUpdateEvent, onDeleteEvent: handleDeleteEvent, onRsvpChange: handleRsvpChange, initialView: 'timeGridWeek', height: '100%', enableCreate: true, enableEdit: true, enableDelete: true, enableDragDrop: true, mode: (userData === null || userData === void 0 ? void 0 : userData.userRole) === 'tutor' || admin ? 'default' : 'view-only', enableResize: true, enableAvailability: true, restrictToAvailableSlots: true, showSessionDetails: true, theme: 'light', timezone: timezone, loading: apiLoading || eventsLoading, onSessionPanelOpenChange: onSessionPanelOpenChange, pendingUserSwitch: pendingUserId, onConfirmUserSwitch: (userId) => {
                        setSelectedTutorId(userId);
                        setPendingUserId(null);
                    }, onCancelUserSwitch: () => setPendingUserId(null), onEventClick: () => { }, onDateSelect: () => { } })) : ((0, jsx_runtime_1.jsx)(Box_1.default, Object.assign({ sx: { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 } }, { children: (0, jsx_runtime_1.jsx)(Typography_1.default, { children: "Please select a tutor to view the calendar" }) }))) }))] }));
    return (0, jsx_runtime_1.jsx)(Box_1.default, Object.assign({ sx: { display: 'flex', flexDirection: 'column', height: '92%' } }, { children: content }));
};
exports.default = CalendarDrawer;
